import { useSessionStore } from './session-store';

export interface TelegramWebAppInitDataUnsafe {
  initData?: string;
  initDataUnsafe?: {
    hash?: string;
    user?: { 
      id: number; 
      username?: string;
      first_name?: string;
      last_name?: string;
    };
    auth_date?: string;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export interface TelegramWebApp {
  initData?: string;
  initDataUnsafe?: TelegramWebAppInitDataUnsafe['initDataUnsafe'];
  version?: string;
  platform?: string;
  themeParams?: Record<string, string>;
  ready: () => void;
  onEvent: (event: string, callback: () => void) => void;
  offEvent: (event: string, callback: () => void) => void;
}

export async function waitForTelegramSDK(timeoutMs = 5000): Promise<TelegramWebApp> {
  const existing = window.Telegram?.WebApp;
  if (existing) {
    return existing;
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Telegram WebApp SDK not available. Please open the app through Telegram or wait for the SDK to load.'));
    }, timeoutMs);

    const handler = () => {
      const webApp = window.Telegram?.WebApp;
      if (webApp) {
        clearTimeout(timer);
        window.removeEventListener('DOMContentLoaded', handler);
        resolve(webApp);
      }
    };

    window.addEventListener('DOMContentLoaded', handler);
    handler();
  });
}

/**
 * Wait for initData to become available in Telegram WebApp SDK
 * Uses fallback on themeChanged event due to Telegram bug where initData
 * may not be available immediately on page load
 * 
 * @param timeoutMs Maximum time to wait for initData (default: 10000ms)
 * @returns initData string or empty string if not available
 */
export async function waitForInitData(timeoutMs = 10000): Promise<string> {
  const webApp = await waitForTelegramSDK();
  
  console.log('[waitForInitData] Starting, timeout:', timeoutMs, 'ms');
  console.log('[waitForInitData] Initial state:', {
    hasWebApp: !!webApp,
    initDataLength: webApp.initData?.length ?? 0,
    initDataPreview: webApp.initData?.substring(0, 100) ?? '',
    initDataUnsafe: webApp.initDataUnsafe ? {
      hasUser: !!webApp.initDataUnsafe.user,
      userId: webApp.initDataUnsafe.user?.id,
    } : null,
  });
  
  // Check if initData is already available
  if (webApp.initData && webApp.initData.trim().length > 0) {
    console.log('[waitForInitData] InitData already available, returning immediately');
    return webApp.initData;
  }
  
  // Wait for initData with fallback on themeChanged event
  return new Promise((resolve) => {
    const startTime = Date.now();
    let resolved = false;
    let lastCheckedValue = '';
    
    const checkInitData = () => {
      if (resolved) return;
      
      const currentInitData = webApp.initData ?? '';
      
      // Log if value changed
      if (currentInitData !== lastCheckedValue) {
        console.log('[waitForInitData] InitData changed:', {
          length: currentInitData.length,
          preview: currentInitData.substring(0, 100),
          elapsed: Date.now() - startTime,
        });
        lastCheckedValue = currentInitData;
      }
      
      if (currentInitData.trim().length > 0) {
        resolved = true;
        cleanup();
        console.log('[waitForInitData] Resolved with initData, length:', currentInitData.length);
        resolve(currentInitData);
        return;
      }
      
      // Check timeout
      const elapsed = Date.now() - startTime;
      if (elapsed >= timeoutMs) {
        resolved = true;
        cleanup();
        console.warn('[waitForInitData] Timeout reached, returning empty string. Elapsed:', elapsed, 'ms');
        resolve(''); // Return empty if timeout
        return;
      }
    };
    
    // Check immediately and then periodically
    checkInitData();
    const intervalId = setInterval(checkInitData, 100);
    
    // Fallback: listen to themeChanged event (Telegram bug workaround)
    const themeChangedHandler = () => {
      console.log('[waitForInitData] themeChanged event fired');
      checkInitData();
    };
    
    webApp.onEvent('themeChanged', themeChangedHandler);
    
    const cleanup = () => {
      clearInterval(intervalId);
      webApp.offEvent('themeChanged', themeChangedHandler);
    };
    
    // Also check on ready event
    try {
      webApp.ready();
    } catch (error) {
      // ready() may throw if already called, ignore
    }
  });
}

/**
 * Get initData from Telegram WebApp SDK (legacy, use waitForInitData instead)
 * Returns empty string if initData is not available
 */
export async function getInitData(): Promise<string> {
  return waitForInitData();
}

/**
 * Get Telegram user ID from WebApp
 * Tries multiple sources to get user ID, even in read-only mode
 * Returns user ID from initDataUnsafe if available (works even without initData)
 */
export function getTelegramUserId(): number | null {
  const fromStore = useSessionStore.getState().telegramUserId;
  if (fromStore) {
    return fromStore;
  }
  try {
    const webApp = window.Telegram?.WebApp;
    if (webApp?.initDataUnsafe?.user?.id) {
      return webApp.initDataUnsafe.user.id;
    }
  } catch (error) {
    console.warn('[Telegram] Failed to get Telegram user ID:', error);
  }
  return null;
}

/**
 * Debug helper function to read and log raw initData from Telegram.WebApp
 * Available in browser console as window.__lexyDebugInitData()
 */
declare global {
  interface Window {
    __lexyDebugInitData?: () => string;
  }
}

if (typeof window !== 'undefined') {
  window.__lexyDebugInitData = () => {
    const raw = window.Telegram?.WebApp?.initData || '';
    console.log('[RAW_INITDATA]', raw);
    return raw;
  };
}
